import { useEffect, useState } from "react";
import { isolateTagNames, openPanel, selectTagNames } from "../../redux/actions";
import { AppState } from "../../redux/stateTypes";
import store from "../../redux/store";
import { Task } from "../../utils/GanttChart";

const openWorkpackagePanel = async (wpIds = []) => {
    const [{ default: WorkPackagePanelComponent }, { WPComponentStyle }] = await Promise.all([
        import("../panels/work-package-panel/WorkPackagePanelComponent"),
        import("../panels/work-package-panel/WPContentComponent")
    ]);
    const currentStore = store.getState() as AppState;
    const dispatch = store.dispatch;
    const openPanels = currentStore.viewer?.openPanels || [];
    const AWPId = null;
    const AWPName = '';
    const workpackagePanelOpened = openPanels.find(
        (panel: any) => panel.PanelId === 'wp-panel-id'
    );
    dispatch(
        openPanel({
            PanelName: 'Work Packages',
            PanelId: 'wp-panel-id',
            PanelClass: WorkPackagePanelComponent,
            Style: WPComponentStyle,
            Data: { wpIds, AWPId, AWPName },
            hasBeenRender: !!workpackagePanelOpened,
            onlyRefresh: !!workpackagePanelOpened
        })
    );
};

export const handleOpenWorkPackagePanel = async () => {
    await openWorkpackagePanel([]);
    // Poll for up to 1 second to detect when the panel appears
    let elapsed = 0;
    const pollInterval = 50;
    const poll = () => {
        const el = document.getElementById('wp-panel-id');
        let isVisible = false;
        if (el) {
            const style = globalThis.getComputedStyle(el);
            isVisible =
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                style.opacity !== '0';
        }
        if (!(!!el && isVisible) && elapsed < 1000) {
            elapsed += pollInterval;
            setTimeout(poll, pollInterval);
        }
    };
    poll();
}

export const useWorkpackagePanelOpened = (openPanels: any[]): boolean => {
    const [opened, setOpened] = useState(false);
    useEffect(() => {
        function isPanelOpen() {
            const el = document.getElementById('wp-panel-id');
            let domOpen = false;
            if (el) {
                const style = globalThis.getComputedStyle(el);
                domOpen =
                    style.display !== 'none' &&
                    style.visibility !== 'hidden' &&
                    style.opacity !== '0';
            }
            const reduxOpen = openPanels.some(
                (panel: any) => panel.PanelId === 'wp-panel-id'
            );
            return domOpen || reduxOpen;
        }
        const checkAndSet = () => setOpened(isPanelOpen());
        checkAndSet();
        const observer = new MutationObserver(checkAndSet);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true
        });
        return () => observer.disconnect();
    }, [openPanels]);
    return opened;
};

export const openSavedPhasing = async (setTasks: any, tasks: any) => {
    const phasingPanelModule = await import("../../controllers/viewer/extension/phasing/PhasingPanel");
    const PhasingPanel = phasingPanelModule.default;
    const { PhasingPanelComponentStyle } = phasingPanelModule;
    const dispatch = store.dispatch;
    const phasingPanelOpened = (store.getState() as AppState).viewer?.openPanels?.some(
        (panel: any) => panel.PanelId === 'phasing-panel-id'
    );
    dispatch(
        openPanel({
            PanelName: '4D Extension',
            PanelId: 'phasing-panel-id',
            PanelClass: PhasingPanel,
            Style: PhasingPanelComponentStyle,
            Data: {
                onGanttChartSelect: (newTasks: Task[]) => setTasks(newTasks),
                tasks: tasks
            },
            hasBeenRender: !!phasingPanelOpened,
            onlyRefresh: !!phasingPanelOpened
        })
    );
}

export const handlePhasingNewWindowEvents = async (event: MessageEvent, newWindow: Window) => {
    if (event.data.type === "PHASING_-OPEN-WORKPACKAGE") {
        handleOpenWorkPackagePanel();
    } else if (event.data.type === 'PHASING_-OPEN-SAVED-GANNT-CHART') {
        await openSavedPhasing((selectedTask: any) => {
            event.source?.postMessage({
                type: 'PHASING_-TASK_SELECTED_FROM_SAVED_GANNT',
                selectedTask: selectedTask,
            });
        }, null);
    } else if (event.data.type === 'PHASING_-OPEN-ISOLATE-TAG-NAMES') {
        const dispatch = store.dispatch;
        dispatch(isolateTagNames(event.data.tagsToIsolate));
    } else if (event.data.type === 'PHASING_SAVE-GANNT-OPEN') {
        await openSavedPhasing((selectedTask: any) => {
            event.source?.postMessage({
                type: 'PHASING_-TASK_SELECTED_FROM_SAVED_GANNT',
                selectedTask: selectedTask,
            });
        }, event.data.tasks);
    } else if (event.data.type === 'PHASING_-HIGHLIGHT-TAG-NAMES') {
        const dispatch = store.dispatch;
        dispatch(isolateTagNames(event.data.tagsToHighlight));
    } else if (event.data.type === 'PHASING_-SELECT-TAG-NAMES') {
        const dispatch = store.dispatch;
        dispatch(selectTagNames(event.data.tags));
    }
}